import { isPlatformServer } from '@angular/common';
import {
  Inject,
  Injectable,
  InjectionToken,
  Optional,
  PLATFORM_ID,
} from '@angular/core';
import { Request } from 'express';

export const REQUEST = new InjectionToken<Request>('REQUEST');

/**
 * IncomingRequestService is meant to perform calculations on incoming
 * request information available via the request object and the platformId.
 *
 * If you want to fetch a specific header in the incoming request, you can
 * perform it in a method here and use it in the project.
 */
@Injectable({
  providedIn: 'root',
})
export class IncomingRequestService {
  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: string,
    // any because type is missing in jest environment, can't find why for now
    @Optional()
    @Inject(REQUEST)
    private readonly request: Record<string, unknown>
  ) {}

  /**
   * Retrieve headers collection inside the server-side request object.
   * If client-side, return empty object.
   */
  get headers(): {} {
    return this.request?.headers || {};
  }

  /**
   * Check if we are server-side (true) or client-side (false).
   */
  get isServerSide(): boolean {
    return isPlatformServer(this.platformId);
  }

  /**
   * Fetch header by name and return it.
   *
   * @param headerName Name of header you want
   * @param defaultValue Value to use by default
   */
  public getHeader(
    headerName: string,
    defaultValue: string = null
  ): string | null {
    return this.headers?.[headerName] || defaultValue;
  }

  /**
   * Check if header exists inside collection.
   *
   * @param headerName Name of header
   */
  public hasHeader(headerName: string): boolean {
    return headerName in this.headers;
  }
}
